/**
 * Coin-based rank-up celebration trigger (specs/coin-based-ranks AC-6..AC-9). The device
 * remembers the last CELEBRATED rank per user; when the coin-derived rank rises we fire ONE
 * Badge Forge ceremony for the highest new rank (a multi-band jump still shows a single ceremony,
 * from the previous rank to the new one). First sync on a device records silently; a lowered rank
 * (backfill/reset) re-baselines silently — rank never demotes in normal play, so this is only a
 * data-safety guard, never a "tier-down" animation.
 *
 * Keyed on RANK (not level): rank is a separate axis from XP level now. Stored under a distinct
 * key so it moves independently of `xp_last_celebrated_level_*`.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../../helpers/logger.helper';
import { useCelebrationStore } from '../stores/celebrationStore';
import { getPeakCoinRankProgress } from './rankRegistry.helper';

const key = (userId: string) => `rank_last_celebrated_${userId}`;

export async function readLastCelebratedRank(userId: string): Promise<number | null> {
  try {
    const raw = Platform.OS === 'web' && typeof window !== 'undefined'
      ? localStorage.getItem(key(userId))
      : await AsyncStorage.getItem(key(userId));
    const parsed = raw == null ? NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (error) {
    if (__DEV__) logger.warn('coinRankCelebrations: read failed', { error });
    return null;
  }
}

export async function writeLastCelebratedRank(userId: string, rank: number): Promise<void> {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      localStorage.setItem(key(userId), String(rank));
      return;
    }
    await AsyncStorage.setItem(key(userId), String(rank));
  } catch (error) {
    if (__DEV__) logger.warn('coinRankCelebrations: write failed', { error });
  }
}

/**
 * Fire at most one rank-up ceremony when the coin-derived rank has increased since last celebrated.
 * @param userId
 * @param rankIndex   Current coin-derived rank (1..10).
 * @param peakCoins   Effective peak coins the rank came from — feeds the ceremony's coin progress bar.
 * @param level       Current XP level, shown on the ceremony chip (display only; does not gate the fire).
 */
export async function maybeCelebrateRank(
  userId: string,
  rankIndex: number,
  peakCoins: number,
  level: number,
): Promise<void> {
  const stored = await readLastCelebratedRank(userId);

  if (stored == null || stored > rankIndex) {
    // First sync (record silently) or a lowered rank (re-baseline) — never celebrate downward.
    await writeLastCelebratedRank(userId, rankIndex);
    return;
  }
  if (rankIndex <= stored) return;

  const { nextEntry } = getPeakCoinRankProgress(peakCoins);
  useCelebrationStore.getState().showRankUp({
    fromRank: rankIndex - 1,
    toRank: rankIndex,
    level,
    // Coin progress toward the next rank threshold (absolute peak vs next entry). At the top rank
    // there is no next entry → a full bar.
    xpCurrent: peakCoins,
    xpTarget: nextEntry ?? peakCoins,
  });
  await writeLastCelebratedRank(userId, rankIndex);
}
